import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Plus, FileText, Mic, Play, Pause, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { PatientNotesDialog } from "./PatientNotesDialog";
import { useRef } from "react";

interface PatientNotesProps {
  patientId: string;
  patientName: string;
}

export function PatientNotes({ patientId, patientName }: PatientNotesProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const { data: notes, isLoading, refetch } = useQuery({
    queryKey: ["patient-notes", patientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("patient_notes")
        .select("*")
        .eq("patient_id", patientId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!patientId,
  });

  const playVoiceMemo = async (noteId: string, filePath: string) => {
    if (playingId === noteId) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }

    const { data } = await supabase.storage.from("voice-memos").createSignedUrl(filePath, 300);
    if (data?.signedUrl) {
      if (audioRef.current) audioRef.current.pause();
      const audio = new Audio(data.signedUrl);
      audioRef.current = audio;
      audio.onended = () => setPlayingId(null);
      audio.play();
      setPlayingId(noteId);
    }
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Patient Notes</h3>
          <p className="text-sm text-muted-foreground">{notes?.length || 0} notes documented</p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Note
        </Button>
      </div>

      {notes && notes.length > 0 ? (
        <div className="space-y-3">
          {notes.map((note) => {
            const services = Array.isArray(note.services) ? note.services : [];
            return (
              <Card key={note.id}>
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="h-3.5 w-3.5" />
                      {format(new Date(note.created_at), "MMM d, yyyy 'at' h:mm a")}
                    </div>
                    <div className="flex items-center gap-2">
                      {note.voice_memo_url && (
                        <Badge variant="outline" className="gap-1">
                          <Mic className="h-3 w-3" />
                          Voice
                        </Badge>
                      )}
                      {note.billing_synced ? (
                        <Badge variant="default" className="text-xs">Synced to Billing</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">Pending Sync</Badge>
                      )}
                    </div>
                  </div>

                  {note.note_text && (
                    <p className="text-sm text-foreground whitespace-pre-wrap mb-3">{note.note_text}</p>
                  )}

                  {note.voice_memo_url && (
                    <div className="flex items-center gap-2 mb-3 p-2 rounded-md bg-muted/50">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => playVoiceMemo(note.id, note.voice_memo_url!)}
                      >
                        {playingId === note.id ? (
                          <Pause className="h-4 w-4" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        Voice memo · {formatDuration(note.voice_memo_duration_seconds)}
                      </span>
                    </div>
                  )}

                  {services.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {services.map((svc: any, i: number) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {svc.label}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <h3 className="font-semibold mb-1">No Notes Yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Add clinical notes with text or voice recordings for this patient.
              </p>
              <Button onClick={() => setDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add First Note
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <PatientNotesDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        patientId={patientId}
        patientName={patientName}
        onNoteSaved={refetch}
      />
    </div>
  );
}
